import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { ActionDropdown } from '@/components';
import { TableData, type TableDataColumn } from '@/design-system/table-data';
import { toast } from '@/design-system/toaster';

import {
  parseWarehouseError,
  splitRetryable,
  warehouseApi,
} from '../../api/warehouse.api';
import { ActionDialog } from '../../components/dialogs';
import {
  BulkFulfillmentDialog,
  isManualUpdatable,
  type ManualFulfillmentRow,
} from '../../components/BulkFulfillmentDialog';
import {
  CarrierChoice,
  carrierBlocked,
  emptyHandover,
  type HandoverDraft,
} from '../../components/CarrierChoice';
import {
  BulkBar,
  EmptyTable,
  ListSearch,
  ListShell,
} from '../../components/ListShell';
import { HeaderActions } from '../../components/pickers';
import {
  codeCls,
  Dash,
  dateCls,
  LoadError,
  MappedBadge,
  metaCls,
  nameCls,
  OrderRefCell,
  PageHeader,
} from '../../components/ui';
import { fmtDate } from '../../format';
import { useCappedSelection } from '../../hooks/useCappedSelection';
import { WarehouseFilterPanel } from '../../components/WarehouseFilterPanel';
import { GOODS_ISSUE_FILTERS } from '../../filterFields';
import { useListQuery } from '../../hooks/useListQuery';
import { useLoad } from '../../hooks/useLoad';
import { shipmentsUrl } from '../../links';
import WarehousePrintModal, {
  type PrintTarget,
} from '../../print/WarehousePrintModal';
import {
  carrierLabel,
  methodLabel,
  SHIPPING_STATUS,
  SHIPPING_TABS,
} from '../../status';
import type { FulfillmentCarrier, GoodsIssueSummary } from '../../types';
import { useWarehouse } from '../../WarehouseContext';
import { WAREHOUSE_PATHS } from '../../paths';

type Tab = (typeof SHIPPING_TABS)[number];

export const AwbCell: React.FC<{ g: GoodsIssueSummary }> = ({ g }) =>
  g.waybill && g.shipmentID ? (
    <Link
      to={shipmentsUrl({ shipmentID: g.shipmentID, waybill: g.waybill })}
      onClick={(e) => e.stopPropagation()}
      className={`${codeCls} text-accent hover:underline underline-offset-4`}
    >
      {g.waybill}
    </Link>
  ) : g.carrier && g.carrier !== 'DASH' ? (
    // A third-party carrier: WMS issued the AWB, there is no Shipments row.
    <span className="flex flex-col">
      <span className={codeCls}>{g.waybill}</span>
      <span className="text-[11px] text-text-sub-600">
        {carrierLabel(g.carrier)}
      </span>
    </span>
  ) : g.status === 'READY_TO_PICKUP' ? (
    <Dash />
  ) : (
    <span className="text-[12px] text-text-sub-600">
      Integrasi Shipments nonaktif
    </span>
  );

const ShippingListPage: React.FC = () => {
  const navigate = useNavigate();
  const { hubID, ready, refreshToken, refresh } = useWarehouse();
  const q = useListQuery<Tab>(SHIPPING_TABS, 'ALL', GOODS_ISSUE_FILTERS);
  const { api: filterApi, apiKey: filterKey } = q.filters;
  const search = q.search.committed || null;
  const selection = useCappedSelection();
  const { selected, setSelected, clear: clearSelection } = selection;
  /** Goods issues whose shipping submit is in flight (row / bulk). */
  const [busyIDs, setBusyIDs] = useState<ReadonlySet<string>>(new Set());
  const submittingRef = useRef(false);
  const [print, setPrint] = useState<PrintTarget | null>(null);
  /** Goods issues waiting for the Submit shipping confirm. */
  const [shipTarget, setShipTarget] = useState<GoodsIssueSummary[] | null>(
    null
  );
  const [carrier, setCarrier] = useState<FulfillmentCarrier>('DASH');
  const [handover, setHandover] = useState<HandoverDraft>(emptyHandover);
  const shipBlocked = carrierBlocked(carrier, handover);
  /** Manually carried orders waiting for the bulk status dialog. */
  const [statusTarget, setStatusTarget] = useState<
    ManualFulfillmentRow[] | null
  >(null);
  const busy = busyIDs.size > 0;

  const list = useLoad(
    (signal) =>
      warehouseApi.listGoodsIssues(
        {
          hubID,
          stage: 'SHIPPING',
          status: q.tab === 'ALL' ? null : q.tab,
          ...filterApi,
          search,
          page: q.page,
          limit: q.limit,
        },
        { signal }
      ),
    [hubID, q.tab, filterKey, search, q.page, q.limit, refreshToken],
    ready
  );
  // Tab counts: the list's filters (minus the tab) — never disagree with it.
  const summary = useLoad(
    (signal) =>
      warehouseApi.getGoodsIssueSummary(
        { hubID, stage: 'SHIPPING', ...filterApi, search },
        { signal }
      ),
    [hubID, filterKey, search, refreshToken],
    ready
  );

  useEffect(() => {
    clearSelection();
  }, [q.tab, q.page, filterKey, hubID, refreshToken, clearSelection]);

  const rows = useMemo(() => list.data?.data ?? [], [list.data]);
  const counts = useMemo(() => {
    const m = new Map<string, number>();
    summary.data?.byStatus.forEach((b) => m.set(b.key, b.count));
    return m;
  }, [summary.data]);
  const tabs = SHIPPING_TABS.map((k) => ({
    key: k,
    label: k === 'ALL' ? 'All' : SHIPPING_STATUS[k].label,
    count: summary.data
      ? k === 'ALL'
        ? summary.data.total
        : (counts.get(k) ?? 0)
      : null,
  }));

  const selectedRows = rows.filter(
    (g) => selected.includes(g.id) && g.status === 'READY_TO_PICKUP'
  );
  /** A shipping row as the bulk status dialog wants it (manual carrier only). */
  const manualRowOf = (g: GoodsIssueSummary): ManualFulfillmentRow | null =>
    g.outboundOrder && isManualUpdatable(g.carrier, g.outboundOrder.status)
      ? {
          outboundOrderID: g.outboundOrder.id,
          code: g.outboundOrder.code,
          waybill: g.waybill,
          carrier: g.carrier ?? null,
          status: g.outboundOrder.status,
          fulfillmentStatus: g.fulfillmentStatus,
        }
      : null;
  const selectedManual = rows
    .filter((g) => selected.includes(g.id))
    .map(manualRowOf)
    .filter((r): r is ManualFulfillmentRow => r !== null);

  /** Row or bulk submit — one at a time; a second click while busy is ignored. */
  const submit = useCallback(
    async (items: GoodsIssueSummary[]) => {
      if (!items.length || submittingRef.current) return;
      submittingRef.current = true;
      setBusyIDs(new Set(items.map((g) => g.id)));
      const refOf = (id: string) => {
        const g = items.find((x) => x.id === id);
        return g?.outboundOrder?.code || g?.reference || g?.code || id;
      };
      try {
        const { results } = await warehouseApi.bulkShip(
          items.map((g) => g.id),
          carrier,
          carrier === 'HANDOVER'
            ? {
                handoverRecipientName: handover.recipientName.trim(),
                ...(handover.note.trim()
                  ? { handoverNote: handover.note.trim() }
                  : {}),
              }
            : undefined
        );
        const ok = results.filter((r) => r.ok);
        const { retryable, others: failed } = splitRetryable(
          results.filter((r) => !r.ok)
        );
        if (ok.length) {
          toast.success(
            `${ok.length} shipping di-submit`,
            carrier === 'HANDOVER'
              ? {
                  description: `Serah terima langsung · Delivered, diterima oleh ${handover.recipientName.trim()}`,
                }
              : carrier !== 'DASH'
                ? {
                    description: `${carrierLabel(carrier)} — update statusnya manual di detail shipping`,
                  }
                : ok.some((r) => r.waybill)
                  ? undefined
                  : { description: 'Integrasi Shipments nonaktif — tanpa waybill' }
          );
        }
        if (failed.length) {
          toast.error(`${failed.length} shipping gagal di-submit`, {
            description: failed
              .slice(0, 3)
              .map(
                (f) =>
                  `${refOf(f.goodsIssueID)}: ${f.error?.message ?? 'gagal'}`
              )
              .join(' · '),
          });
        }
        if (retryable.length) {
          // Transient lock / deadlock: nothing changed for these, safe to resend.
          const again = items.filter((g) =>
            retryable.some((r) => r.goodsIssueID === g.id)
          );
          toast.warning(
            `${retryable.length} shipping bentrok dengan proses lain`,
            {
              description: `${again
                .slice(0, 3)
                .map((g) => refOf(g.id))
                .join(', ')} — aman untuk dicoba lagi.`,
              action: {
                label: 'Coba lagi',
                onClick: () => void retryRef.current(again),
              },
            }
          );
        }
      } catch (err) {
        toast.error('Submit shipping gagal', {
          description: parseWarehouseError(err).message,
        });
      } finally {
        submittingRef.current = false;
        setBusyIDs(new Set());
        clearSelection();
        refresh();
      }
    },
    [carrier, handover, clearSelection, refresh]
  );
  const retryRef = useRef(submit);
  retryRef.current = submit;

  const columns = useMemo<TableDataColumn<GoodsIssueSummary>[]>(
    () => [
      {
        key: 'order',
        title: 'Request / External ID',
        render: (_v, g) => (
          <OrderRefCell order={g.outboundOrder} reference={g.reference} />
        ),
      },
      {
        key: 'code',
        title: 'Picking order',
        render: (_v, g) => <span className={codeCls}>{g.code}</span>,
      },
      {
        key: 'client',
        title: 'Client',
        render: (_v, g) => (
          <span className={nameCls}>
            {g.client?.name ?? `Client #${g.clientID}`}
          </span>
        ),
      },
      {
        key: 'method',
        title: 'Method',
        render: (_v, g) => (
          <span className={metaCls}>
            {methodLabel(g.outboundOrder?.method)}
          </span>
        ),
      },
      {
        key: 'expected',
        title: 'Expected',
        render: (_v, g) =>
          g.outboundOrder ? (
            <span className={dateCls}>
              {fmtDate(g.outboundOrder.expectedDeliveryDate)}
            </span>
          ) : (
            <Dash />
          ),
      },
      {
        key: 'sj',
        title: 'Surat jalan',
        render: (_v, g) => (
          <span className={codeCls}>{g.suratJalanNo ?? '—'}</span>
        ),
      },
      {
        key: 'shipStatus',
        title: 'Status',
        render: (_v, g) => (
          <MappedBadge
            map={SHIPPING_STATUS}
            value={g.shippingStatus || g.status}
          />
        ),
      },
      {
        key: 'awb',
        title: 'Shipment (Waybill)',
        render: (_v, g) => <AwbCell g={g} />,
      },
      {
        key: 'hub',
        title: 'Hub',
        render: (_v, g) => (
          <span className={nameCls}>{g.hub?.name ?? `Hub #${g.hubID}`}</span>
        ),
      },
      {
        key: 'actions',
        title: '',
        align: 'right',
        render: (_v, g) => (
          <ActionDropdown
            busy={busyIDs.has(g.id)}
            actions={[
              {
                key: 'view',
                label: 'View details',
                icon: 'ri-eye-line',
                onClick: () => navigate(`${WAREHOUSE_PATHS.shipping}/${g.id}`),
              },
              {
                key: 'submit',
                label: 'Submit shipping',
                icon: 'ri-send-plane-line',
                hidden: g.status !== 'READY_TO_PICKUP',
                disabled: busyIDs.size > 0,
                onClick: () => setShipTarget([g]),
              },
              {
                key: 'status',
                label: 'Update status',
                icon: 'ri-truck-line',
                hidden: !manualRowOf(g),
                onClick: () => {
                  const row = manualRowOf(g);
                  if (row) setStatusTarget([row]);
                },
              },
              {
                key: 'shipments',
                label: 'Lihat di Shipments',
                icon: 'ri-external-link-line',
                hidden: !g.waybill || !g.shipmentID,
                onClick: () =>
                  navigate(
                    shipmentsUrl({
                      shipmentID: g.shipmentID,
                      waybill: g.waybill,
                    })
                  ),
              },
              {
                key: 'sj',
                label: 'Print surat jalan',
                icon: 'ri-printer-line',
                hidden: !g.suratJalanNo,
                onClick: () => setPrint({ kind: 'sj', id: g.id }),
              },
              {
                key: 'picking',
                label: 'Print picking list',
                icon: 'ri-printer-line',
                onClick: () => setPrint({ kind: 'picking', id: g.id }),
              },
            ]}
          />
        ),
      },
    ],
    [navigate, busyIDs]
  );

  return (
    <div className="space-y-5 p-6">
      <PageHeader
        eyebrow="Warehouse · Outbound management · Goods issue"
        title="Shipping"
        actions={<HeaderActions />}
      />
      <ListShell
        tabs={tabs}
        activeTab={q.tab}
        onTabChange={q.setTab}
        filters={
          <>
            <ListSearch
              value={q.search.value}
              onChange={q.search.setValue}
              placeholder="Search order, SJ, waybill…"
            />
            <WarehouseFilterPanel filters={q.filters} title="Filter shipping" />
          </>
        }
        bulkBar={
          // Two kinds of row can be picked: Ready to pick up (submit) and
          // manually carried (status). The bar names each with its count.
          <BulkBar
            count={selectedRows.length + selectedManual.length}
            noun="dokumen dipilih"
            actionLabel={
              selectedRows.length
                ? `Submit shipping (${selectedRows.length})`
                : `Update status (${selectedManual.length})`
            }
            actionIcon={
              selectedRows.length ? 'ri-send-plane-line' : 'ri-truck-line'
            }
            secondary={
              selectedRows.length && selectedManual.length
                ? {
                    label: `Update status (${selectedManual.length})`,
                    icon: 'ri-truck-line',
                    onAction: () => setStatusTarget(selectedManual),
                  }
                : null
            }
            busy={busy}
            capped={selection.capped}
            onClear={clearSelection}
            onAction={() =>
              selectedRows.length
                ? setShipTarget(selectedRows)
                : setStatusTarget(selectedManual)
            }
          />
        }
        pagination={{
          page: q.page,
          limit: q.limit,
          total: list.data?.pagination.total ?? 0,
          onPageChange: q.setPage,
          onLimitChange: q.setLimit,
        }}
      >
        {list.error ? (
          <LoadError message={list.error.message} onRetry={list.reload} />
        ) : (
          <TableData
            size="sm"
            loading={list.loading || !ready}
            data={rows}
            columns={columns}
            selection={{
              selectedKeys: selected,
              onSelectionChange: setSelected,
              getRowKey: (g) => g.id,
              isRowSelectable: (g) =>
                g.status === 'READY_TO_PICKUP' || !!manualRowOf(g),
            }}
            onRowClick={(g) => navigate(`${WAREHOUSE_PATHS.shipping}/${g.id}`)}
            emptyState={<EmptyTable title="Tidak ada shipping" />}
          />
        )}
      </ListShell>
      <ActionDialog
        open={!!shipTarget}
        title="Submit shipping"
        subtitle={
          shipTarget && shipTarget.length === 1
            ? shipTarget[0].outboundOrder?.code || shipTarget[0].code
            : `${shipTarget?.length ?? 0} dokumen dipilih`
        }
        description="Stok dipotong. Bisa dibatalkan selama barang masih di hub."
        confirmLabel={
          shipTarget && shipTarget.length > 1
            ? `Submit shipping (${shipTarget.length})`
            : 'Submit shipping'
        }
        icon="ri-send-plane-line"
        busy={busy}
        testId="shipping-submit-dialog"
        confirmDisabled={!!shipBlocked}
        disabledReason={shipBlocked}
        onClose={() => setShipTarget(null)}
        onConfirm={() => {
          const items = shipTarget ?? [];
          void submit(items).then(() => setShipTarget(null));
        }}
      >
        <CarrierChoice
          value={carrier}
          onChange={setCarrier}
          handover={handover}
          onHandoverChange={setHandover}
          disabled={busy}
        />
      </ActionDialog>
      <BulkFulfillmentDialog
        rows={statusTarget}
        onClose={() => setStatusTarget(null)}
        onDone={() => {
          setStatusTarget(null);
          clearSelection();
          refresh();
        }}
      />
      <WarehousePrintModal target={print} onClose={() => setPrint(null)} />
    </div>
  );
};

export default ShippingListPage;
